import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import { useDashboardLearnerContext } from '@features/dashboard'
import {
  getPublishedShortVideosPage,
  toggleShortVideoSubscribe,
} from '@services/firebase/short-videos'
import { useShortVideosState } from '@features/short-videos/state/useShortVideosState'
import { normalizeShortVideoLocale } from '@features/short-videos/shared/short-video-helpers'
import {
  getCurrentVideoSubscribe,
  getFilteredShortVideos,
} from '@features/short-videos/shared/short-video-selectors'
import {
  LmsButton,
} from '@shared/ui'
import { ShortVideoCardCollection } from '@features/short-videos/shared/ShortVideoCardCollection'
import { ShortVideosStatusBlock } from '@features/short-videos/shared/ShortVideosStatusBlock'
import { ShortVideoChannelCard } from './ShortVideoChannelCard'
import { ShortVideoSearchDialog } from './ShortVideoSearchDialog'

export function ShortVideoListScreen() {
  const { enqueueSnackbar } = useSnackbar()
  const { i18n } = useTranslation('translation')
  const { state: dashboardState } = useDashboardLearnerContext()
  const { actions, state } = useShortVideosState()
  const [mode, setMode] = useState<'list' | 'channels'>('list')
  const locale = normalizeShortVideoLocale(i18n.language)
  const filteredVideos = useMemo(
    () =>
      getFilteredShortVideos({
        selectedTopicIds: state.selectedTopicIds,
        videos: state.videos,
      }),
    [state.selectedTopicIds, state.videos],
  )
  const isPagePreparing = state.loading.page || (!state.feedLoaded && !state.errorMessage)
  const canShowPageEmpty =
    !isPagePreparing && !state.errorMessage && state.feedLoaded && filteredVideos.length === 0

  const handleToggleSubscribe = async (channelId: string) => {
    if (!dashboardState.myUser) {
      return
    }

    const subscribe = getCurrentVideoSubscribe({
      channelId,
      subscribes: state.subscribes,
      userId: dashboardState.myUser.id,
    })

    try {
      const result = await toggleShortVideoSubscribe({
        channelId,
        currentSubscribe: subscribe,
        profile: dashboardState.myUser,
      })

      actions.setSubscribes(
        result
          ? [...state.subscribes.filter((item) => item.id !== result.id), result]
          : state.subscribes.filter((item) => item.id !== subscribe?.id),
      )
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Subscribe failed.', {
        variant: 'error',
      })
    }
  }

  const handleNextPage = async () => {
    if (state.videos.length === 0) {
      return
    }

    try {
      actions.setPageLoading(true)
      const result = await getPublishedShortVideosPage({
        anchorId: state.videos[0].id,
        direction: 'next',
      })

      if (result.videos.length === 0) {
        actions.setPagination({
          ...state.pagination,
          canGoNext: false,
        })
        return
      }

      const nextOffset = state.pagination.offset + state.videos.length
      actions.setVideos(result.videos)
      actions.setPagination({
        canGoNext: nextOffset + result.videos.length < result.total,
        canGoPrev: true,
        historySizes: [...state.pagination.historySizes, state.videos.length],
        offset: nextOffset,
        total: result.total,
      })
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to load more videos.', {
        variant: 'error',
      })
    } finally {
      actions.setPageLoading(false)
    }
  }

  const handlePreviousPage = async () => {
    if (state.videos.length === 0 || !state.pagination.canGoPrev) {
      return
    }

    try {
      actions.setPageLoading(true)
      const result = await getPublishedShortVideosPage({
        anchorId: state.videos[0].id,
        direction: 'prev',
      })

      if (result.videos.length === 0) {
        return
      }

      const nextHistorySizes = state.pagination.historySizes.slice(0, -1)
      const nextOffset = Math.max(0, state.pagination.offset - result.videos.length)

      actions.setVideos(result.videos)
      actions.setPagination({
        canGoNext: nextOffset + result.videos.length < result.total,
        canGoPrev: nextHistorySizes.length > 0,
        historySizes: nextHistorySizes,
        offset: nextOffset,
        total: result.total,
      })
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to load previous videos.', {
        variant: 'error',
      })
    } finally {
      actions.setPageLoading(false)
    }
  }

  if (mode === 'channels') {
    return <ShortVideoChannelListScreen onReturn={() => { setMode('list') }} onToggleSubscribe={handleToggleSubscribe} />
  }

  return (
    <div className="shortVideosCatalogListSurface">
      <div className="shortVideosCatalogSearchStrip">
        <LmsButton
          aria-label="Search short videos"
          className="shortVideosCatalogSearchTrigger"
          leadingIcon={<Search aria-hidden="true" />}
          onClick={() => {
            actions.setSearchDialogOpen(true)
          }}
          variant="outline"
        >
          Search
        </LmsButton>
      </div>

      <section className="shortVideosCatalogWrapper">
        <div className="shortVideosTitleRow">
          <h2 className="shortVideosSectionTitle">Channels</h2>
          <LmsButton
            className="shortVideosTextLink"
            onClick={() => {
              setMode('channels')
            }}
            size="sm"
            variant="ghost"
          >
            View all ({state.channels.length})
          </LmsButton>
        </div>
        <div className="shortVideosChannelsRail">
          {state.channels.slice(0, 12).map((channel) => (
            <ShortVideoChannelCard
              channel={channel}
              compact
              key={channel.id}
              onToggleSubscribe={() => void handleToggleSubscribe(channel.id)}
              subscribeCount={
                state.subscribes.filter((subscribe) => subscribe.channelId === channel.id).length
              }
              subscribed={getCurrentVideoSubscribe({
                channelId: channel.id,
                subscribes: state.subscribes,
                userId: dashboardState.myUser?.id,
              })}
            />
          ))}
        </div>
      </section>

      <section className="shortVideosCatalogWrapper">
        <div className="shortVideosTitleRow">
          <h2 className="shortVideosSectionTitle">Topics</h2>
          <span className="shortVideosTextMeta">All ({state.topics.length})</span>
        </div>
        <div className="shortVideosTopicList">
          {state.topics.map((topic) => {
            const selected = state.selectedTopicIds.includes(topic.id)

            return (
              <LmsButton
                aria-pressed={selected}
                className={`shortVideosTopicChip ${selected ? 'isSelected' : ''}`}
                key={topic.id}
                onClick={() => {
                  actions.toggleSelectedTopic(topic.id)
                }}
                size="sm"
                variant="ghost"
              >
                {topic.title ?? topic.id}
              </LmsButton>
            )
          })}
        </div>
      </section>

      <section className="shortVideosCatalogWrapper">
        <div className="shortVideosTitleRow">
          <h2 className="shortVideosSectionTitle">Sample suggested sentences</h2>
        </div>

        {state.errorMessage ? (
          <ShortVideosStatusBlock body={state.errorMessage} title="We could not load short videos" tone="error" />
        ) : null}

        {isPagePreparing ? (
          <ShortVideosStatusBlock body="Keep this page open while we prepare the selected short videos." isLoading title="Preparing short videos" />
        ) : canShowPageEmpty ? (
          <ShortVideosStatusBlock body="This page loaded successfully, but no published short videos match the selected topics." title="No short videos match these topics" tone="empty" />
        ) : (
          <ShortVideoCardCollection
            cellClassName="shortVideosVideoCell"
            channels={state.channels}
            gridClassName="shortVideosVideoGrid"
            locale={locale}
            onSelect={actions.setSelectedVideo}
            videos={filteredVideos}
          />
        )}

        <div className="shortVideosPagination">
          <LmsButton
            disabled={!state.pagination.canGoPrev || isPagePreparing}
            leadingIcon={<ChevronLeft aria-hidden="true" />}
            mobileFullWidth
            onClick={() => void handlePreviousPage()}
            variant="outline"
          >
            Prev
          </LmsButton>
          <LmsButton
            disabled={!state.pagination.canGoNext || isPagePreparing}
            mobileFullWidth
            onClick={() => void handleNextPage()}
            trailingIcon={<ChevronRight aria-hidden="true" />}
            variant="primary"
          >
            Next
          </LmsButton>
        </div>
      </section>

      <ShortVideoSearchDialog />
    </div>
  )
}

function ShortVideoChannelListScreen({
  onReturn,
  onToggleSubscribe,
}: {
  readonly onReturn: () => void
  readonly onToggleSubscribe: (channelId: string) => Promise<void>
}) {
  const { state } = useShortVideosState()
  const { state: dashboardState } = useDashboardLearnerContext()
  const [visibleCount, setVisibleCount] = useState(60)

  return (
    <div className="shortVideosCatalogListSurface">
      <div className="shortVideosCatalogSearchStrip">
        <LmsButton
          aria-label="Back to short videos"
          leadingIcon={<ChevronLeft aria-hidden="true" />}
          onClick={onReturn}
          size="icon"
          variant="ghost"
        />
      </div>

      <section className="shortVideosCatalogWrapper shortVideosChannelListSurface">
        <div className="shortVideosChannelGrid">
          {state.channels.slice(0, visibleCount).map((channel) => (
            <div className="shortVideosChannelCell" key={channel.id}>
              <ShortVideoChannelCard
                channel={channel}
                onToggleSubscribe={() => void onToggleSubscribe(channel.id)}
                subscribeCount={
                  state.subscribes.filter((subscribe) => subscribe.channelId === channel.id).length
                }
                subscribed={getCurrentVideoSubscribe({
                  channelId: channel.id,
                  subscribes: state.subscribes,
                  userId: dashboardState.myUser?.id,
                })}
              />
            </div>
          ))}
        </div>

        {visibleCount < state.channels.length ? (
          <LmsButton
            className="shortVideosLoadMoreButton"
            onClick={() => {
              setVisibleCount((value) => value + 60)
            }}
            variant="secondary"
          >
            Load more
          </LmsButton>
        ) : null}
      </section>
    </div>
  )
}
